// Public reset-password page. Reads the single-use token from the URL, takes a new password
// (same min-12 policy as the profile change-password form), and submits to /api/auth/reset.
// On success all the user's sessions are already revoked server-side, so it routes to /login.

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { postAuth } from '../lib/authPublic';

const PASSWORD_MIN = 12;
const INVALID = 'This reset link is invalid or has expired. Request a new one.';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  // No token in the URL → nothing to do; show the generic invalid message immediately.
  if (!token) {
    return (
      <Shell title="Reset your password">
        <div style={S.errorBox} role="alert">{INVALID}</div>
        <Link to="/forgot" style={S.link}>Request a new reset link</Link>
      </Shell>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (pw !== confirm) { setError('New password and confirmation do not match.'); return; }
    if (pw.length < PASSWORD_MIN) { setError(`New password must be at least ${PASSWORD_MIN} characters.`); return; }
    setLoading(true);
    const res = await postAuth('/api/auth/reset', { token, new_password: pw });
    setLoading(false);
    if (res.error || res.status !== 200) { setError(res.error ?? INVALID); return; }
    setDone(true);
    setTimeout(() => navigate('/login', { replace: true }), 1200);
  }

  if (done) {
    return (
      <Shell title="Password reset">
        <div style={S.infoBox} role="status">Password reset — please sign in with your new password.</div>
        <Link to="/login" style={S.link}>Go to sign in</Link>
      </Shell>
    );
  }

  return (
    <Shell title="Choose a new password">
      <form onSubmit={handleSubmit} style={S.form} noValidate>
        <div style={S.field}>
          <label style={S.label} htmlFor="pw">New password</label>
          <input id="pw" type="password" autoComplete="new-password" required
            value={pw} onChange={(e) => setPw(e.target.value)}
            style={S.input} placeholder="At least 12 characters" disabled={loading} />
        </div>
        <div style={S.field}>
          <label style={S.label} htmlFor="confirm">Confirm new password</label>
          <input id="confirm" type="password" autoComplete="new-password" required
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            style={S.input} disabled={loading} />
        </div>
        {error && <div style={S.errorBox} role="alert">{error}</div>}
        <button type="submit" style={S.button} disabled={loading || !pw || !confirm}>
          {loading ? 'Resetting…' : 'Reset password'}
        </button>
        <Link to="/login" style={S.link}>← Back to sign in</Link>
      </form>
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.header}><h1 style={S.title}>{title}</h1></div>
        {children}
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  card: { width: '100%', maxWidth: 400, background: '#1e293b', borderRadius: 12, padding: '40px 36px', boxShadow: '0 4px 24px rgba(0,0,0,0.4)' },
  header: { textAlign: 'center', marginBottom: 28 },
  title: { margin: 0, fontSize: 24, fontWeight: 700, color: '#f1f5f9', letterSpacing: -0.5 },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 500, color: '#94a3b8' },
  input: { padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', fontSize: 15, outline: 'none' },
  button: { padding: 12, background: '#3b82f6', border: 'none', borderRadius: 6, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  errorBox: { padding: '10px 12px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#fca5a5', fontSize: 13, lineHeight: 1.5 },
  infoBox: { padding: '12px 14px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#bbf7d0', fontSize: 14, lineHeight: 1.5, marginBottom: 16 },
  link: { display: 'block', textAlign: 'center', fontSize: 13, color: '#7dd3fc', textDecoration: 'none', marginTop: 12 },
};
